/**
 * Collection of common transformation utilities
 * Provides reusable transformation functions for common data operations
 */
export class TransformationUtils {
  /**
   * Transform database snake_case to camelCase
   */
  static snakeToCamel(value: string): string {
    return value.replaceAll(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  }

  /**
   * Transform camelCase to database snake_case
   */
  static camelToSnake(value: string): string {
    return value.replaceAll(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  }

  /**
   * Transform an object's keys from snake_case to camelCase
   */
  static objectSnakeToCamel<T = Record<string, unknown>>(obj: Record<string, unknown>): T {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = this.snakeToCamel(key);
      result[camelKey] = value;
    }
    return result as T;
  }

  /**
   * Transform an object's keys from camelCase to snake_case
   */
  static objectCamelToSnake<T = Record<string, unknown>>(obj: Record<string, unknown>): T {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = this.camelToSnake(key);
      result[snakeKey] = value;
    }
    return result as T;
  }

  /**
   * Parse a JSON string with error handling
   */
  static parseJson<T = unknown>(jsonString: string | null | undefined, defaultValue?: T): T | undefined {
    if (!jsonString) return defaultValue;
    
    try {
      return JSON.parse(jsonString) as T;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Safely convert string to number
   */
  static toNumber(value: unknown, defaultValue: number = 0): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value);
      return Number.isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  }

  /**
   * Safely convert value to integer
   */
  static toInteger(value: unknown, defaultValue: number = 0): number {
    if (typeof value === 'number') return Math.floor(value);
    if (typeof value === 'string') {
      const parsed = Number.parseInt(value, 10);
      return Number.isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  }

  /**
   * Safely convert value to boolean
   */
  static toBoolean(value: unknown, defaultValue: boolean = false): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const lower = value.toLowerCase();
      return lower === 'true' || lower === '1' || lower === 'yes';
    }
    if (typeof value === 'number') return value !== 0;
    return defaultValue;
  }

  /**
   * Ensure value is a string
   */
  static toString(value: unknown, defaultValue: string = ''): string {
    if (value === null || value === undefined) return defaultValue;
    return String(value);
  }

  /**
   * Transform date string to ISO format
   */
  static toISODate(value: unknown): string | undefined {
    if (!value) return undefined;
    
    try {
      const date = new Date(value as string | number | Date);
      return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    } catch {
      return undefined;
    }
  }

  /**
   * Transform date to YYYY-MM-DD format
   */
  static toDateString(value: unknown): string | undefined {
    if (!value) return undefined;
    
    try {
      const date = new Date(value as string | number | Date);
      return Number.isNaN(date.getTime()) ? undefined : date.toISOString().split('T')[0];
    } catch {
      return undefined;
    }
  }

  /**
   * Clean and format phone number
   */
  static cleanPhone(value: unknown): string {
    if (!value) return '';
    const phone = String(value);
    return phone.replaceAll(/\D/g, '');
  }

  /**
   * Clean and normalize email
   */
  static cleanEmail(value: unknown): string {
    if (!value) return '';
    return String(value).toLowerCase().trim();
  }

  /**
   * Transform array of strings to array, handling comma-separated strings
   */
  static toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string') {
      return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
    }
    return [];
  }

  /**
   * Remove null and undefined values from object
   */
  static removeNullish<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined) {
        result[key] = value;
      }
    }
    return result as Partial<T>;
  }

  /**
   * Pick specific fields from an object
   */
  static pick<T extends Record<string, unknown>, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
    const result: Record<string, unknown> = {};
    for (const key of keys) {
      if (key in obj) {
        result[key as string] = obj[key];
      }
    }
    return result as Pick<T, K>;
  }

  /**
   * Omit specific fields from an object
   */
  static omit<T extends Record<string, unknown>, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
    const result: Record<string, unknown> = {};
    const keysToOmit = new Set(keys);
    for (const [key, value] of Object.entries(obj)) {
      if (!keysToOmit.has(key as K)) {
        result[key] = value;
      }
    }
    return result as Omit<T, K>;
  }

  /**
   * Deep clone an object
   */
  static deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj) as unknown as T;
    if (Array.isArray(obj)) return obj.map(item => this.deepClone(item)) as unknown as T;
    
    const cloned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      cloned[key] = this.deepClone(value);
    }
    return cloned as T;
  }

  /**
   * Merge multiple objects deeply
   */
  static deepMerge<T extends Record<string, unknown>>(...objects: Partial<T>[]): T {
    const result: Record<string, unknown> = {};
    
    for (const obj of objects) {
      for (const [key, value] of Object.entries(obj)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          result[key] = this.deepMerge(result[key] as Record<string, unknown> || {}, value as Record<string, unknown>);
        } else {
          result[key] = value;
        }
      }
    }
    
    return result as T;
  }
}